import { makeStyles, mergeClasses, tokens, Tooltip } from "@fluentui/react-components";
import { ArrowMaximize16Regular, QuestionCircle16Regular } from "@fluentui/react-icons";
import { useState, type ReactNode } from "react";
import { AppDialog } from "@/components/app-dialog";
import { useDataTableStyles } from "@/components/data-table";
import { FuiButton } from "@/components/fui-control";
import { cellAt, type VisualTable } from "./visual-table";
import { visualCard, visualDescription, visualHead, visualMeta, visualTitle } from "./visual-styles";

export type { VisualTable, VisualTableColumn, VisualTableRow } from "./visual-table";

const useStyles = makeStyles({
  card: visualCard,
  compact: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    gap: tokens.spacingVerticalXS,
  },
  head: { ...visualHead, alignItems: "flex-start" },
  heading: { display: "flex", flexDirection: "column", gap: "2px", minWidth: 0, flex: "1 1 auto" },
  titleRow: { display: "flex", alignItems: "center", gap: "2px", minWidth: 0 },
  title: { ...visualTitle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  description: visualDescription,
  side: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: "58%",
    minHeight: "24px",
    "& > button": { flexShrink: 0 },
  },
  meta: visualMeta,
  chart: { width: "100%", minWidth: 0 },
  focusBody: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM, minWidth: 0 },
  focusTable: { maxHeight: "36vh" },
  row: {},
  rowInteractive: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
  },
  lastCell: { borderBottom: "none" },
});

export interface ReportVisualProps {
  title: string;
  /** 무엇을 읽는지 한 줄(예: "막대를 누르면 그 구분의 변경 내역으로 이동") */
  description?: string;
  /** 제목 옆 ? 에 띄울 설명 */
  help?: ReactNode;
  /** 기준·건수 같은 우측 메타 */
  meta?: string;
  /** 크게 보기에서 차트 아래 보여 줄 같은 데이터 */
  table?: VisualTable;
  height?: number;
  /** 크게 보기에서 차트 높이 */
  focusHeight?: number;
  /** 카드 여백을 줄인다. 분석 그리드처럼 차트가 여러 장 붙는 곳. */
  compact?: boolean;
  /** 두 번째 인자가 true면 크게 보기. 카드에는 일부만, 대화상자에는 전체를 그릴 때 쓴다. */
  chart?: (height: number, expanded?: boolean) => ReactNode;
  /** 항상 보이는 머리 동작(예: 전체 보기) */
  extra?: ReactNode;
  /** 차트·표 아래에 항상 붙는 내용(정리 필요 리본 등) */
  footer?: ReactNode;
  className?: string;
  /** 차트가 없을 때의 내용(목록·빈 상태) */
  children?: ReactNode;
}

/**
 * 요약·분석·리포트가 공유하는 Visual 컨테이너.
 * 제목 · 한 줄 설명 · 메타 + 크게 보기 아이콘(상시 노출). 카드에는 그림자를 쓰지 않는다.
 */
export function ReportVisual({ title, description, help, meta, table, height = 180, focusHeight = 360, compact = false, chart, extra, footer, className, children }: ReportVisualProps) {
  const styles = useStyles();
  const [focus, setFocus] = useState(false);
  const hasChart = Boolean(chart);

  return (
    <section className={mergeClasses(styles.card, compact && styles.compact, className)} aria-label={title}>
      <div className={styles.head}>
        <div className={styles.heading}>
          <div className={styles.titleRow}>
            <span className={styles.title} title={title}>
              {title}
            </span>
            {help && (
              <Tooltip content={<span>{help}</span>} relationship="description" withArrow positioning="above">
                <FuiButton density="dialog" appearance="subtle" icon={<QuestionCircle16Regular />} aria-label={`${title} 설명`} />
              </Tooltip>
            )}
          </div>
          {description && (
            <span className={styles.description} title={description}>
              {description}
            </span>
          )}
        </div>
        <div className={styles.side}>
          {meta && (
            <span className={styles.meta} title={meta}>
              {meta}
            </span>
          )}
          {hasChart && (
            <Tooltip content="크게 보기" relationship="label">
              <FuiButton density="dialog" appearance="subtle" icon={<ArrowMaximize16Regular />} aria-label={`${title} 크게 보기`} onClick={() => setFocus(true)} />
            </Tooltip>
          )}
          {extra}
        </div>
      </div>

      {hasChart ? (
        <div className={styles.chart} style={{ minHeight: height }}>
          {chart!(height, false)}
        </div>
      ) : (
        children
      )}
      {footer}

      {hasChart && (
        <AppDialog open={focus} onClose={() => setFocus(false)} title={title} description={description ?? meta} width="xl">
          <div className={styles.focusBody}>
            <div className={styles.chart} style={{ minHeight: focusHeight }}>
              {focus && chart!(focusHeight, true)}
            </div>
            {table && <VisualTableView table={table} className={styles.focusTable} />}
          </div>
        </AppDialog>
      )}
    </section>
  );
}

/** 차트와 같은 사실을 담은 표. 행에 이동이 있으면 행 클릭·첫 칸 버튼(키보드)으로 드릴스루한다. */
export function VisualTableView({ table, className }: { table: VisualTable; className?: string }) {
  const ts = useDataTableStyles();
  const styles = useStyles();
  const last = table.rows.length - 1;
  return (
    <div className={mergeClasses(ts.frame, className)}>
      <table className={ts.table} aria-label={table.ariaLabel}>
        <thead>
          <tr>
            {table.columns.map((c) => (
              <th key={c.key} scope="col" className={mergeClasses(ts.th, c.numeric && ts.num)}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, ri) => {
            const activate = row.onActivate;
            return (
              <tr key={row.key} className={mergeClasses(styles.row, activate && styles.rowInteractive)} onClick={activate}>
                {table.columns.map((c, ci) => {
                  const value = cellAt(row, ci);
                  return (
                    <td key={c.key} className={mergeClasses(ts.td, c.numeric && ts.num, ri === last && styles.lastCell)}>
                      {ci === 0 && activate ? (
                        <button
                          type="button"
                          className={ts.linkBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            activate();
                          }}
                          aria-label={row.activateLabel ? `${value} · ${row.activateLabel}` : String(value)}
                        >
                          {value}
                        </button>
                      ) : (
                        value
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
